/**
 * Contrat de lecture des données Vevidental ingérées.
 *
 * L'ingestion remplissait la base sans qu'aucun écran ne la lise : 2 953
 * enregistrements que personne ne pouvait consulter. Ces schémas sont le pont.
 */
import { z } from "zod";

/** Libellés humains des ressources — partagés serveur/front, une seule source. */
export const RESSOURCES_VEVIDENTAL: Record<string, string> = {
  clinics: "Cabinets",
  doctors: "Clients (praticiens)",
  works: "Travaux",
  work_products: "Produits vendus",
  invoices: "Factures",
  delivery_notes: "Bons de livraison",
  products: "Catalogue produits",
  materials: "Matériaux",
  providers: "Fournisseurs",
  technicians: "Employés",
  machines: "Machines",
  stages: "Étapes",
  tasks: "Tâches",
  incidents: "Incidences",
  orders: "Commandes",
};

export const RessourceVevidentalSchema = z.object({
  ressource: z.string(),
  libelle: z.string(),
  elements: z.number().int().min(0),
  dernierSuccesAt: z.string().nullable(),
  derniereErreur: z.string().nullable(),
  derniereErreurAt: z.string().nullable(),
  /**
   * « Jamais synchronisé » et « à jour mais vide » sont deux états DIFFÉRENTS.
   * Les confondre ferait croire à une panne là où il n'y a rien à remonter —
   * et inversement laisserait croire qu'une ressource est vide alors qu'on ne
   * l'a simplement jamais demandée.
   */
  jamaisSynchronise: z.boolean(),
  /** rattrapage en cours : page à laquelle le prochain passage reprendra */
  reprendreA: z.number().int().nullable(),
  /**
   * Total mesuré À LA SOURCE, indépendamment de notre parcours (dichotomie sur
   * les pages). `null` = jamais mesuré — état DIFFÉRENT de « mesuré à zéro » :
   * les confondre ferait passer une ressource jamais contrôlée pour saine.
   */
  totalSource: z.number().int().min(0).nullable(),
  /** Éléments annoncés par la source et absents de la copie. `null` = non mesuré. */
  manquants: z.number().int().min(0).nullable(),
});
export type RessourceVevidental = z.infer<typeof RessourceVevidentalSchema>;

/**
 * Le délai RÉEL réception → livraison, par famille de produit.
 *
 * Vient des bons de livraison — 69 960 lignes ingérées et jamais lues jusqu'au
 * 2026-08-12. C'est ce qui permet de PROMETTRE un délai plutôt que de
 * l'estimer. On expose le p90, pas la médiane : promettre la médiane, c'est
 * être en retard une fois sur deux.
 */
export const DelaiFamilleSchema = z.object({
  famille: z.string(),
  travaux: z.number().int().min(0),
  medianeJours: z.number().min(0),
  p90Jours: z.number().min(0),
  /** false = trop peu de travaux pour promettre quoi que ce soit */
  promettable: z.boolean(),
});

export const VevidentalApercuSchema = z.object({
  ressources: z.array(RessourceVevidentalSchema),
  /**
   * Délais mesurés. `depuis` n'est pas un détail : avant 2024 le délai vaut
   * 1 jour pour TOUS les travaux — une reprise de données, pas une
   * performance — et l'écran doit dire sur quoi il compte.
   */
  delais: z.object({
    depuis: z.string(),
    travauxMesures: z.number().int().min(0),
    partPromettable: z.number().min(0).max(100),
    familles: z.array(DelaiFamilleSchema),
  }),
  /** Écart avec ce que le CRM sert aujourd'hui — le chiffre qui motive tout. */
  ecart: z.object({
    cabinetsVevidental: z.number().int().min(0),
    cabinetsCrm: z.number().int().min(0),
  }),
});
export type VevidentalApercu = z.infer<typeof VevidentalApercuSchema>;

export const VevidentalRechercheSchema = z.object({
  ressource: z.string(),
  libelle: z.string(),
  /** total DANS LA BASE : dit s'il y a plus que ce qui est affiché */
  total: z.number().int().min(0),
  affiches: z.number().int().min(0),
  items: z.array(z.object({ id: z.string(), payload: z.record(z.unknown()) })),
});
export type VevidentalRecherche = z.infer<typeof VevidentalRechercheSchema>;

/**
 * Colonnes affichées par ressource.
 *
 * Un rendu générique de vingt-sept champs serait illisible ; on choisit ce qui
 * identifie l'élément d'un coup d'œil. Le reste du payload reste en base et
 * reste cherchable — rien n'est perdu, seulement rangé.
 */
export const COLONNES_VEVIDENTAL: Record<string, Array<{ cle: string; titre: string }>> = {
  clinics: [
    { cle: "code", titre: "Code" }, { cle: "name", titre: "Cabinet" },
    { cle: "town", titre: "Ville" }, { cle: "cp", titre: "CP" }, { cle: "email", titre: "E-mail" },
  ],
  doctors: [
    { cle: "id", titre: "Id" }, { cle: "name", titre: "Client" },
    { cle: "town", titre: "Ville" }, { cle: "email", titre: "E-mail" }, { cle: "phone", titre: "Téléphone" },
  ],
  works: [
    { cle: "code", titre: "Travail" }, { cle: "clinic.name", titre: "Cabinet" },
    { cle: "doctor.name", titre: "Client" }, { cle: "patient.name", titre: "Patient" },
    { cle: "status_name", titre: "Statut" },
    { cle: "accept_date", titre: "Reçu" }, { cle: "finish_date", titre: "Fin" },
  ],
  invoices: [
    { cle: "code", titre: "Facture" }, { cle: "clinic.name", titre: "Cabinet" },
    { cle: "date", titre: "Date" }, { cle: "due_date", titre: "Échéance" },
    { cle: "total_amount", titre: "Total" }, { cle: "paid", titre: "Payée" },
  ],
  delivery_notes: [
    { cle: "code", titre: "Bon" }, { cle: "clinic.name", titre: "Cabinet" },
    { cle: "delivery_note_date", titre: "Date" }, { cle: "status_name", titre: "Statut" },
    { cle: "total_price_with_vat", titre: "Total TTC" },
  ],
  work_products: [
    { cle: "product.name", titre: "Produit" }, { cle: "work.code", titre: "Travail" },
    { cle: "teeth", titre: "Dents" }, { cle: "units", titre: "Qté" }, { cle: "total_price", titre: "Total" },
  ],
  products: [
    { cle: "code", titre: "Code" }, { cle: "name", titre: "Produit" },
    { cle: "category.name", titre: "Catégorie" }, { cle: "price", titre: "Prix" },
  ],
  materials: [
    { cle: "code", titre: "Code" }, { cle: "name", titre: "Matériau" },
    { cle: "manufacturer", titre: "Fabricant" }, { cle: "storage_amount", titre: "Stock" },
  ],
  providers: [
    { cle: "code", titre: "Code" }, { cle: "name", titre: "Fournisseur" },
    { cle: "town", titre: "Ville" }, { cle: "email", titre: "E-mail" },
  ],
  technicians: [
    { cle: "id", titre: "Id" }, { cle: "name", titre: "Prénom" },
    { cle: "surname", titre: "Nom" }, { cle: "email", titre: "E-mail" },
  ],
  machines: [
    { cle: "name", titre: "Machine" }, { cle: "manufacturer", titre: "Fabricant" },
    { cle: "model", titre: "Modèle" }, { cle: "serial", titre: "N° de série" },
  ],
  stages: [
    { cle: "id", titre: "Id" }, { cle: "name", titre: "Étape" }, { cle: "description", titre: "Description" },
  ],
};

/** Colonnes de repli : toute ressource non décrite reste consultable. */
export const COLONNES_DEFAUT = [
  { cle: "id", titre: "Id" },
  { cle: "code", titre: "Code" },
  { cle: "name", titre: "Nom" },
];

/* -------------------------------------------------- liens vers le portail */

/**
 * Lien direct vers un dossier Vevidental.
 *
 * `url` est nulle quand le rapprochement n'a pas abouti, et `motif` dit
 * pourquoi. La distinction compte à l'écran : « introuvable » se règle en
 * complétant une fiche, « ambigu » se règle en dédoublonnant chez Vevidental —
 * deux gestes différents, faits par deux personnes différentes.
 */
export const LienVevidentalSchema = z.object({
  url: z.string().nullable(),
  motif: z.enum(["ambigu", "introuvable"]).nullable(),
  via: z.enum(["email", "nom"]).nullable(),
});
export type LienVevidental = z.infer<typeof LienVevidentalSchema>;

export const LiensVevidentalSchema = z.object({
  cabinet: LienVevidentalSchema,
  praticiens: z.array(LienVevidentalSchema.extend({ id: z.string(), nom: z.string() })),
});
export type LiensVevidental = z.infer<typeof LiensVevidentalSchema>;

/** Explication affichable d'un rapprochement manqué. */
export function motifLienLisible(motif: LienVevidental["motif"]): string {
  if (motif === "ambigu") {
    return "plusieurs dossiers Vevidental portent ce nom — aucun lien n'est proposé pour ne pas ouvrir le mauvais";
  }
  return "aucun dossier Vevidental correspondant n'a été trouvé";
}

/** Lit `clinic.name` dans un payload imbriqué, sans lancer d'exception. */
export function valeurChemin(payload: Record<string, unknown>, chemin: string): string {
  let courant: unknown = payload;
  for (const part of chemin.split(".")) {
    if (courant === null || typeof courant !== "object") return "";
    courant = (courant as Record<string, unknown>)[part];
  }
  if (courant === null || courant === undefined) return "";
  if (typeof courant === "object") return "";
  return String(courant);
}

/* --------------------------------------------- ce qu'un cabinet commande */

/**
 * Le mix produits d'un cabinet, sur deux fenêtres de douze mois.
 *
 * Un classement seul dit ce qu'il commande ; il ne dit pas ce qui BOUGE. La
 * fenêtre précédente est donc servie avec la récente — c'est la variation qui
 * déclenche un appel, pas le rang.
 */
export const ProduitCabinetSchema = z.object({
  nom: z.string(),
  famille: z.string(),
  recent: z.number().int().min(0),
  precedent: z.number().int().min(0),
  montantRecent: z.number(),
});

export const MixProduitsSchema = z.object({
  cabinetIdVevidental: z.number().int().positive(),
  totalRecent: z.number().int().min(0),
  totalPrecedent: z.number().int().min(0),
  produits: z.array(ProduitCabinetSchema),
  familles: z.array(
    z.object({
      famille: z.string(),
      recent: z.number().int().min(0),
      precedent: z.number().int().min(0),
    }),
  ),
});
export type MixProduits = z.infer<typeof MixProduitsSchema>;
